/**
 * The share modal (SPEC-TINYURL §3.2, §3.3, §3.4).
 *
 * The report dialog's sibling, not a new pattern: native `<dialog>`, focus
 * returned to the opener explicitly, dismissal work done where the dismissal is
 * initiated rather than hung off the `close` event.
 *
 * ## Three states, and the third is the one that must look deliberate
 *
 * `minting` -> `ready`, or `minting` -> `fallback`. The fallback shows the
 * canonical long URL with a quiet note, and **Copy still works** — §3.2's rule
 * is that sharing never hard-fails, so the worst outcome is a longer link
 * rather than a modal that cannot answer. It is also the state a local dev
 * server shows unless the dev plugin is running, which makes it the state most
 * likely to be seen during development and the one least likely to be polished
 * if it were treated as an error path.
 *
 * ## The card preview
 *
 * An `<img>` at the real card URL, which needed `og.tensorlens.dev` in
 * `img-src` (owner ruling, 2026-08-16). A client-side re-render would have
 * needed no CSP change and would show what the card *ought* to be — which is
 * precisely the claim a preview exists to check. Per OG-SPEC §8 a
 * not-yet-generated card answers with the generic image at 200, so the preview
 * always renders something and never blocks the modal.
 */

import { cardUrlFor } from "../render/card-url.js";
import { type MintResult, mintLink } from "../share/mint-link.js";
import type { UrlState } from "./url-state.js";

export interface ShareDialogDeps {
  readonly mint: (state: UrlState) => Promise<MintResult>;
  readonly cardUrl: (state: UrlState) => Promise<string>;
  readonly copy: (text: string) => Promise<void>;
}

/**
 * Clipboard write, with the selection fallback for insecure contexts.
 *
 * `navigator.clipboard` is undefined on plain HTTP, and a share modal that
 * throws there would be a hard failure in the one flow that promises never to
 * have one.
 */
async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.append(field);
  field.select();
  document.execCommand("copy");
  field.remove();
}

export const DEFAULT_SHARE_DEPS: ShareDialogDeps = {
  mint: (state) => mintLink(state),
  cardUrl: (state) => cardUrlFor(state.repoId, state.rev, state.part),
  copy: copyText,
};

/*
 * The copy affordance sits **beside the field**, not down in the actions row.
 *
 * The row is where a dialog puts what it does *to itself* — Close, and before
 * this, Copy. But copying is something done to the link, and a control for it
 * three elements away asks the visitor to connect the two. Hugging Face's
 * copy-model-id button is the same shape of decision, and the reason it works
 * is proximity rather than the icon.
 *
 * The button holds one glyph and keeps one job. Its success state is not inside
 * it at all — see `#share-copy-status` below.
 */
const COPY_GLYPH = `<svg class="share-glyph share-glyph-copy" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><rect x="9" y="9" width="11" height="11" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;

/*
 * `#share-copy-status` is the copy confirmation — **shown and announced**, which
 * is why it is one element rather than two.
 *
 * An earlier version swapped the button's glyph to a tick and put the words in
 * a hidden live region. That confirms twice in two places, and the visible half
 * lived inside the control rather than beside the thing that was copied. A
 * green line under the link says it once, to everyone: `role="status"` makes it
 * announce, and the tick is `aria-hidden` so a screen reader is not told
 * "check mark" before the sentence.
 *
 * It is separate from `#share-note` deliberately — that element carries the
 * fallback-link message, and two things writing to one live region means
 * whichever ran last silently replaces the other.
 *
 * This note lives here rather than as an HTML comment inside the template
 * below, and not only for tidiness: the text names two ids, and writing them in
 * backticks *inside a template literal closes it*. That is the trap
 * `infra/lib/site-stack.ts` carries a warning about for the CloudFront function
 * source, and it cost a parse error here before this comment moved.
 */
function markup(): string {
  return `
<form method="dialog" class="share-body">
  <h2 id="share-title">Share this view</h2>
  <p class="share-intro" id="share-intro">
    Short link to the model and revision you are looking at.
  </p>

  <label class="share-url-label" for="share-url">Link</label>
  <div class="share-url-row">
    <input id="share-url" class="share-url" type="text" readonly value="" />
    <button
      type="button"
      class="glyph-button share-copy"
      id="share-copy"
      aria-label="Copy link"
      title="Copy link"
      disabled
    >${COPY_GLYPH}</button>
  </div>

  <p class="share-note" id="share-note" role="status" aria-live="polite"></p>
  <p class="share-copied" id="share-copy-status" role="status" aria-live="polite" hidden>
    <span aria-hidden="true">&check;</span> Copied to clipboard
  </p>

  <figure class="share-preview">
    <img id="share-card" class="share-card" alt="" width="1200" height="630" />
    <figcaption>Preview image shown when sharing this link.</figcaption>
  </figure>

  <div class="share-actions">
    <button type="button" class="share-close" id="share-close">Close</button>
  </div>
</form>`;
}

export function openShareDialog(
  state: UrlState,
  opener: HTMLElement,
  deps: ShareDialogDeps = DEFAULT_SHARE_DEPS,
): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "share-dialog";
  dialog.setAttribute("aria-labelledby", "share-title");
  dialog.setAttribute("aria-describedby", "share-intro");
  dialog.innerHTML = markup();
  document.body.append(dialog);

  const field = dialog.querySelector("#share-url") as HTMLInputElement;

  /*
   * Select the link without lighting up the focus ring.
   *
   * A text input **always** matches `:focus-visible` while focused, however
   * focus arrived — that is the spec's heuristic, not a browser quirk, because
   * a text field accepts keystrokes and the user needs to know where they go.
   * So the global ring fires on our own `select()`, and the field flashes an
   * outline at the exact moment someone clicks Copy: two things changing for
   * one action.
   *
   * The class suppresses it for *programmatic* selection only, and is dropped
   * on blur — so tabbing to the field afterwards rings it normally. Suppressing
   * `:focus-visible` outright would have been one line and would have taken the
   * indicator away from keyboard users on a control that is still a tab stop.
   *
   * The selection highlight remains, which is the feedback that matters here:
   * it shows *what* is selected rather than merely that something is.
   */
  const selectQuietly = (): void => {
    field.classList.add("is-autoselected");
    field.select();
  };
  field.addEventListener("blur", () => field.classList.remove("is-autoselected"));
  const note = dialog.querySelector("#share-note") as HTMLElement;
  const card = dialog.querySelector("#share-card") as HTMLImageElement;
  const copy = dialog.querySelector("#share-copy") as HTMLButtonElement;
  const copyStatus = dialog.querySelector("#share-copy-status") as HTMLElement;
  const closeButton = dialog.querySelector("#share-close") as HTMLButtonElement;

  /*
   * The same latch the report dialog documents. A **scripted** `close()` fires
   * no `close` event in Chrome, so cleanup hung off that listener never runs
   * for a dismissal the page initiates — leaving the dialog in the document and
   * its ids resolving inside a closed element. The work happens where the
   * dismissal starts; the latch is what makes both routes safe.
   */
  let dismissed = false;
  function close(): void {
    if (dismissed) return;
    dismissed = true;
    dialog.close();
    dialog.remove();
    opener.focus();
  }

  closeButton.addEventListener("click", close);
  dialog.addEventListener("close", close);

  /*
   * Click outside to dismiss — the same as pressing Close.
   *
   * A `<dialog>`'s backdrop is not a separate element: a click on it targets the
   * dialog itself, which is what `event.target === dialog` reads. Two things
   * make that insufficient on its own, and both bite in this particular modal.
   *
   * **Padding is inside the element.** `.share-dialog` has `--space-4` of it, so
   * a click just inside the border also targets the dialog. Closing on that
   * means the modal vanishes when someone clicks a few pixels off a control,
   * which reads as a bug rather than a shortcut. Comparing against the bounding
   * rect excludes it, since padding is within the rect and the backdrop is not.
   *
   * **A drag can start inside and end outside.** The link field is there to be
   * selected, and a select-and-release that overshoots the edge fires a click
   * whose coordinates are outside — dismissing the dialog mid-selection, having
   * thrown away the selection. So the press must have started outside too.
   *
   * Escape needs nothing here: `showModal` gives it natively, and it arrives
   * through the `close` listener above.
   */
  const startedOutside = (event: MouseEvent): boolean => {
    const rect = dialog.getBoundingClientRect();
    return (
      event.clientY < rect.top ||
      event.clientY > rect.bottom ||
      event.clientX < rect.left ||
      event.clientX > rect.right
    );
  };

  let pressBeganOutside = false;
  dialog.addEventListener("mousedown", (event) => {
    pressBeganOutside = event.target === dialog && startedOutside(event);
  });
  dialog.addEventListener("click", (event) => {
    /*
     * `target === dialog` also excludes keyboard activation. Pressing Enter on
     * a button dispatches a click at coordinates `(0, 0)` in some browsers,
     * which is "outside" by any geometric test — so without this, using Copy
     * from the keyboard would close the modal.
     */
    if (pressBeganOutside && event.target === dialog && startedOutside(event)) {
      close();
    }
  });

  // `minting`: a link is coming, and Copy has nothing to give yet.
  field.value = "";
  note.textContent = "Making a short link…";
  copy.disabled = true;

  dialog.showModal();
  closeButton.focus();

  // The preview does not gate the link. It is the slower of the two and the
  // less important, so it lands whenever it lands.
  void deps.cardUrl(state).then((url) => {
    card.src = url;
    card.alt = state.repoId
      ? `Share card for ${state.repoId}`
      : "TensorLens share card";
  });

  void deps.mint(state).then((result) => {
    if (dismissed) return;
    field.value = result.url;
    copy.disabled = false;

    if (result.kind === "short") {
      note.textContent = "";
      note.hidden = true;
    } else {
      /*
       * The fallback wording says what the visitor got, not what went wrong.
       * "Short link unavailable" is actionable — the link in the box works —
       * where a reason code would be an apology for a thing they cannot fix.
       */
      note.hidden = false;
      note.textContent = "Short link unavailable — this full link works the same.";
    }
    selectQuietly();
  });

  copy.addEventListener("click", () => {
    void deps.copy(field.value).then(
      () => {
        /*
         * The glyph becomes a tick and the modal stays open: people copy, then
         * decide where to paste (§3.3).
         *
         * The tick is *visual* feedback and a screen reader sees none of it,
         * which is why the live region below says so in words. A confirmation
         * that only some visitors receive is not a confirmation.
         */
        copyStatus.toggleAttribute("hidden", false);
        setTimeout(() => {
          /*
           * Hidden again rather than emptied.
           *
           * A live region announces on *change*, so leaving the text in place
           * and toggling visibility means a second copy re-announces — which
           * emptying the node would also achieve, at the cost of the markup
           * living in JavaScript instead of in the template.
           */
          copyStatus.toggleAttribute("hidden", true);
          /*
           * Four seconds, not two.
           *
           * A confirmation is read *after* the action, often after the eye has
           * already moved on to where the link is going — so it has to survive
           * a glance back. Two seconds was gone before that glance (owner,
           * 2026-08-16), which leaves someone unsure whether the copy happened
           * and clicking again to find out.
           *
           * The cost of it lingering is nothing: it sits in its own line under
           * the field and moves no layout when it goes.
           */
        }, 4000);
      },
      () => {
        // Even the clipboard failing must not strand anyone: the field is
        // selected and readable, so the link is still gettable by hand.
        note.hidden = false;
        note.textContent = "Could not copy — the link is selected, copy it as usual.";
        selectQuietly();
      },
    );
  });

  return dialog;
}
